import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useClaimDenialReport,
    useExportClaimDenials,
    type ClaimDenialFilter,
} from '@/api/reports';
import { usePayers } from '@/api/payers';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Claim Denial Analysis — denial reason × payer × CPT rollup.
 *
 * Headline view is the per-combo table so a biller can spot
 * "Aetna denies 97153 for missing modifier 17 times this quarter" at a
 * glance. Single-axis rollups sit alongside for the simpler asks
 * ("which payer denies the most?", "which reason hurts most?").
 */
export function ClaimDenialPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<ClaimDenialFilter>({});
    const [draft,  setDraft]  = useState<ClaimDenialFilter>({});

    const { data, isLoading, isError, error, isFetching } = useClaimDenialReport(filter);
    const exportM = useExportClaimDenials();
    const payersQuery = usePayers();

    const payerOptions = useMemo(() => {
        if (!payersQuery.data) return [];
        return [...payersQuery.data]
            .filter((p) => p.status === 'Active')
            .map((p) => ({ id: p.id, label: p.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [payersQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* surfaced below */ }
    }

    const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Claim Denial Analysis</h1>
                    <p className="text-xs text-text-subtle">
                        Denial reason &times; payer &times; CPT rollup. Date filter is applied to date of service.
                    </p>
                </div>
                {canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Payer</span>
                            <select
                                value={draft.payerId ?? ''}
                                onChange={(e) => setDraft({ ...draft, payerId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All payers</option>
                                {payerOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">CPT code</span>
                            <Input
                                value={draft.serviceCode ?? ''}
                                onChange={(e) => setDraft({ ...draft, serviceCode: e.target.value.toUpperCase() || undefined })}
                                placeholder="e.g. 97153"
                                maxLength={12}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">DOS from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">DOS to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn&apos;t load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Kpi label="Denied claims" value={data.kpis.deniedCount.toLocaleString()} />
                        <Kpi label="Filed claims"  value={data.kpis.totalFiled.toLocaleString()} />
                        <Kpi label="Denial rate"   value={`${data.kpis.denialRatePct}%`} />
                        <Kpi label="Denied amount" value={fmtMoney(data.kpis.deniedAmount)} />
                        <Kpi label="Reasons / payers" value={`${data.kpis.distinctReasons} / ${data.kpis.distinctPayers}`} />
                    </div>

                    <Card>
                        <CardHeader>
                            <div>
                                <CardTitle>Reason &times; Payer &times; CPT</CardTitle>
                                <CardSubtitle>
                                    Top combos drive re-work priorities. Sort is by frequency.
                                </CardSubtitle>
                            </div>
                        </CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.byCombo.length === 0 ? (
                                <Empty>No denied claims match the current filter.</Empty>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Reason</th>
                                            <th className="px-3 py-2 font-bold">Payer</th>
                                            <th className="px-3 py-2 font-bold">CPT</th>
                                            <th className="px-3 py-2 font-bold text-right">Count</th>
                                            <th className="px-3 py-2 font-bold text-right">Billed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byCombo.map((r, idx) => (
                                            <tr key={idx} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.reason}</td>
                                                <td className="px-3 py-2 text-xs text-text-muted">{r.payerName}</td>
                                                <td className="px-3 py-2 text-xs tabular-nums">{r.cpt}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.billedAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <RollupCard title="By reason" subtitle="Where denials cluster.">
                            {data.byReason.length === 0
                                ? <Empty>No denied claims match.</Empty>
                                : <CountTable rows={data.byReason.map((r) => ({ label: r.reason, count: r.count, billed: r.billedAmount }))} firstColLabel="Reason" />}
                        </RollupCard>
                        <RollupCard title="By payer" subtitle="Which payers deny most.">
                            {data.byPayer.length === 0
                                ? <Empty>No denied claims match.</Empty>
                                : <CountTable rows={data.byPayer.map((r) => ({ label: r.payerName, count: r.count, billed: r.billedAmount }))} firstColLabel="Payer" />}
                        </RollupCard>
                        <RollupCard title="By CPT" subtitle="Which codes fail.">
                            {data.byCpt.length === 0
                                ? <Empty>No denied claims match.</Empty>
                                : <CountTable rows={data.byCpt.map((r) => ({ label: r.cpt, count: r.count, billed: r.billedAmount }))} firstColLabel="CPT" />}
                        </RollupCard>
                    </div>

                    <Card>
                        <CardHeader>
                            <div>
                                <CardTitle>Denied claims</CardTitle>
                                <CardSubtitle>Click a claim # in Billing to open the record. Newest DOS first.</CardSubtitle>
                            </div>
                        </CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.denials.length === 0 ? (
                                <Empty>No denied claims match the current filter.</Empty>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">DOS</th>
                                            <th className="px-3 py-2 font-bold">Claim #</th>
                                            <th className="px-3 py-2 font-bold">Client</th>
                                            <th className="px-3 py-2 font-bold">Payer</th>
                                            <th className="px-3 py-2 font-bold">CPT</th>
                                            <th className="px-3 py-2 font-bold">Reason</th>
                                            <th className="px-3 py-2 font-bold text-right">Billed</th>
                                            <th className="px-3 py-2 font-bold text-right">Paid</th>
                                            <th className="px-3 py-2 font-bold text-right">Adj</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.denials.map((d) => (
                                            <tr key={d.id} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{d.dos || '—'}</td>
                                                <td className="px-3 py-2 text-xs tabular-nums">
                                                    <Link to={`/billing/${d.id}`} className="text-accent-text hover:underline">{d.displayId}</Link>
                                                </td>
                                                <td className="px-3 py-2 text-xs">{d.clientName}</td>
                                                <td className="px-3 py-2 text-xs text-text-muted">{d.payerName}</td>
                                                <td className="px-3 py-2 text-xs tabular-nums">{d.serviceCode}</td>
                                                <td className="px-3 py-2 text-xs">{d.denialReason || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(d.billedAmount)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums text-text-muted">{fmtMoney(d.paidAmount)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums text-text-muted">{fmtMoney(d.adjAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className="font-display text-xl font-bold mt-1 tabular-nums text-text">{value}</p>
            </CardBody>
        </Card>
    );
}

function RollupCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
    return (
        <Card>
            <CardHeader>
                <div>
                    <CardTitle>{title}</CardTitle>
                    <CardSubtitle>{subtitle}</CardSubtitle>
                </div>
            </CardHeader>
            <CardBody className="pt-0 overflow-x-auto">{children}</CardBody>
        </Card>
    );
}

function CountTable({ rows, firstColLabel }: { rows: { label: string; count: number; billed: number }[]; firstColLabel: string }) {
    const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                    <th className="px-3 py-2 font-bold">{firstColLabel}</th>
                    <th className="px-3 py-2 font-bold text-right">Count</th>
                    <th className="px-3 py-2 font-bold text-right">Billed</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.label} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2">{r.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-text-muted">{fmtMoney(r.billed)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <p className="text-xs text-text-subtle px-3 py-4">{children}</p>;
}
